/**
 * ProShip Direct v2 — will this rule actually fire?
 *
 * Set up one order, see which rule wins and which courier it picks. Live, not behind a Run button:
 * the evaluation is pure and instant, and the point is to watch the verdict move as you change the
 * rule you are writing.
 *
 * THE FAILURE IT EXISTS TO CATCH is the rule that never fires. Rules are evaluated top to bottom,
 * first match wins, so "COD above 2kg" written below "all COD" is dead — and nothing on the rules
 * list says so, because both rules are valid, enabled and correct in isolation. So the trace lists
 * every rule it walked past, not just the winner, and marks the ones that would have matched but
 * were already beaten as SHADOWED.
 *
 * The draft being edited is spliced into the live set at the position it would occupy, so a merchant
 * finds out here rather than after saving.
 */
import React, { useMemo, useState } from 'react';
import {
  Box, Grid, Typography, TextField, MenuItem, Chip, Tooltip, Alert,
} from '@mui/material';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import { sectionCardSx } from '../../../styles/patterns';
import { SUBTLE, NAVY, BORDER, LIGHT_BG, WHITE, SUCCESS, WARNING, ERROR, PRIMARY_BLUE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { RULE_CONDITION_TYPES } from '../data/defaults';
import { DEFAULT_SIM_ORDER, simulateAllocation, describeCondition } from '../data/simulateAllocation';
import { MONO } from './directCellStyles';
import { OwnerChip } from './chips';

const smallSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.5 };
const strongSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };
const fieldSx = {
  '& .MuiInputBase-input': { fontFamily: FONT, fontSize: 12 },
  '& .MuiInputLabel-root': { fontSize: 10 },
};

/** How each outcome reads, and what it means the merchant should do about it. */
const OUTCOME = {
  MATCHED: { label: 'Wins', color: SUCCESS, icon: CheckCircleIcon, note: 'This rule decides the courier' },
  CATCH_ALL: { label: 'Wins (catch-all)', color: SUCCESS, icon: CheckCircleIcon, note: 'No conditions, so it takes anything left' },
  SHADOWED: { label: 'Shadowed', color: WARNING, icon: LayersOutlinedIcon, note: 'Would have matched, but a rule above already won' },
  FAILED: { label: 'No match', color: SUBTLE, icon: RemoveCircleOutlineIcon, note: null },
  DISABLED: { label: 'Off', color: SUBTLE, icon: BlockOutlinedIcon, note: null },
};

/** One order attribute, rendered by the kind of condition it feeds. */
function SimField({ field, value, onChange }) {
  if (field.kind === 'choice' || field.kind === 'multi') {
    return (
      <TextField
        select
        size='small'
        fullWidth
        label={field.label}
        value={value ?? ''}
        onChange={(e) => onChange(field.key, e.target.value)}
        sx={fieldSx}
      >
        {(field.options || []).map((o) => (
          <MenuItem key={o.value} value={o.value} sx={{ fontFamily: FONT, fontSize: 12 }}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }
  return (
    <TextField
      size='small'
      fullWidth
      label={field.unit ? `${field.label} (${field.unit})` : field.label}
      // A time window is tested against a clock, so the sample order needs a clock input.
      type={field.kind === 'compare' ? 'number' : field.kind === 'between' ? 'time' : 'text'}
      value={value ?? ''}
      placeholder={field.placeholder}
      onChange={(e) => onChange(field.key, e.target.value)}
      sx={fieldSx}
    />
  );
}

export default function AllocationSimulator({ rules, pool, draft, draftPosition = 0 }) {
  const [order, setOrder] = useState(DEFAULT_SIM_ORDER);
  const set = (key, value) => setOrder((prev) => ({ ...prev, [key]: value }));

  /**
   * The rule set to walk.
   *
   * Disabled rules are NOT filtered out — evaluateRules marks them Off and keeps them in the trace,
   * because "that rule would have won, but it is switched off" is one of the answers a merchant
   * comes here for. Filtering them would remove the row that explains the surprise.
   *
   * A draft, where one is passed, is spliced in at the position it would occupy.
   */
  const effectiveRules = useMemo(() => {
    const live = rules;
    if (!draft) return live;
    const asRule = {
      id: '__draft__',
      name: draft.name?.trim() || 'This rule (unsaved)',
      conditions: draft.conditions || [],
      strategy: draft.strategy,
      order: draft.order || [],
      enabled: true,
      isDraft: true,
    };
    const at = Math.min(Math.max(0, draftPosition), live.length);
    return [...live.slice(0, at), asRule, ...live.slice(at)];
  }, [rules, draft, draftPosition]);

  const result = useMemo(
    () => simulateAllocation({ order, rules: effectiveRules, pool }),
    [order, effectiveRules, pool]
  );

  const draftRow = result.rows.find((r) => r.rule.isDraft);
  const winner = result.winner;

  return (
    <Box sx={sectionCardSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, mb: 0.25 }}>
        <ScienceOutlinedIcon sx={{ fontSize: 18, color: PRIMARY_BLUE }} />
        <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>
          Try an order
        </Typography>
      </Box>
      <Typography sx={{ ...smallSx, mb: 1.5 }}>
        Which rule wins, and which courier it picks. Updates as you type.
      </Typography>

      {/* ── The order ── */}
      <Grid container spacing={1.25} sx={{ mb: 1.75 }}>
        {RULE_CONDITION_TYPES.map((field) => (
          <Grid item xs={6} md={4} lg={3} key={field.key}>
            <SimField field={field} value={order[field.key]} onChange={set} />
          </Grid>
        ))}
      </Grid>

      {/* ── The verdict ── */}
      {result.matched ? (
        <Box
          sx={{
            p: 1.5,
            mb: 1.5,
            borderRadius: RADIUS.md,
            border: `1px solid ${SUCCESS}44`,
            bgcolor: `${SUCCESS}0a`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, flexWrap: 'wrap' }}>
            <CheckCircleIcon sx={{ fontSize: 18, color: SUCCESS }} />
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: NAVY }}>
                {winner ? winner.name : 'No courier available'}
                {winner && (
                  <Box component='span' sx={{ fontFamily: MONO, fontSize: SIZE['3xs'], color: SUBTLE, ml: 0.75 }}>
                    {winner.detail}
                  </Box>
                )}
              </Typography>
              <Typography sx={smallSx}>
                via <b>{result.matched.rule.name}</b> · {result.basis}
                {winner ? ` · ${winner.why}` : ''}
              </Typography>
            </Box>
            {winner && <OwnerChip owner={winner.owner} />}
          </Box>
          {!winner && (
            <Typography sx={{ ...smallSx, mt: 0.5, color: ERROR }}>
              The rule matched but its priority list is empty, or no courier in the pool is switched
              on. Allocation would fall through to the engine default.
            </Typography>
          )}
        </Box>
      ) : (
        <Alert severity='warning' sx={{ mb: 1.5, fontFamily: FONT, fontSize: SIZE['2xs'] }}>
          No rule matches this order, and there is no catch-all. Allocation falls through to the
          engine default — which works, but nothing you configured decided it.
        </Alert>
      )}

      {/* The one thing a merchant writing a rule most needs to know. */}
      {draftRow && draftRow.outcome === 'SHADOWED' && (
        <Alert severity='warning' sx={{ mb: 1.5, fontFamily: FONT, fontSize: SIZE['2xs'] }}>
          This rule would have matched, but <b>{result.matched?.rule.name}</b> sits above it and won
          first. For this order your rule never fires — move it up, or narrow the rule above it.
        </Alert>
      )}

      {/* ── The trace ── */}
      <Typography sx={{ ...strongSx, mb: 0.75 }}>Rules, in order</Typography>
      <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: RADIUS.md, overflow: 'hidden' }}>
        {result.rows.map((row, i) => {
          const meta = OUTCOME[row.outcome];
          const Icon = meta.icon;
          return (
            <Box
              key={row.rule.id}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: GAP.sm,
                px: 1.25,
                py: 0.85,
                bgcolor: row.rule.isDraft ? `${PRIMARY_BLUE}0a` : WHITE,
                borderTop: i === 0 ? 'none' : `1px solid ${BORDER}`,
              }}
            >
              <Typography sx={{ ...smallSx, fontFamily: MONO, minWidth: 16, pt: '2px' }}>{i + 1}</Typography>
              <Icon sx={{ fontSize: 15, color: meta.color, mt: '2px', flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                  <Typography sx={strongSx}>{row.rule.name}</Typography>
                  {row.rule.isDraft && (
                    <Chip
                      size='small'
                      label='this rule'
                      sx={{
                        height: 17,
                        fontFamily: FONT,
                        fontSize: SIZE['4xs'],
                        fontWeight: WEIGHT.semibold,
                        bgcolor: `${PRIMARY_BLUE}1f`,
                        color: PRIMARY_BLUE,
                      }}
                    />
                  )}
                </Box>
                {meta.note && <Typography sx={smallSx}>{meta.note}</Typography>}
                {row.failed.length > 0 && (
                  <Typography sx={smallSx}>
                    Needs {row.failed.map(describeCondition).join(', and ')}
                  </Typography>
                )}
              </Box>
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: SIZE['4xs'],
                  fontWeight: WEIGHT.semibold,
                  color: meta.color,
                  whiteSpace: 'nowrap',
                  pt: '2px',
                }}
              >
                {meta.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* ── Ranked pool ── */}
      {result.ranked.length > 1 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ ...strongSx, mb: 0.5 }}>Order it would try them in</Typography>
          <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
            {result.ranked.slice(0, 6).map((c, i) => (
              <Tooltip key={c.key} title={c.why}>
                <Chip
                  size='small'
                  label={`${i + 1}. ${c.name}`}
                  sx={{
                    fontFamily: FONT,
                    fontSize: SIZE['4xs'],
                    height: 22,
                    bgcolor: i === 0 ? `${SUCCESS}14` : LIGHT_BG,
                    color: i === 0 ? SUCCESS : SUBTLE,
                    fontWeight: i === 0 ? WEIGHT.semibold : WEIGHT.regular,
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        </Box>
      )}

      <Typography sx={{ ...smallSx, mt: 1.5 }}>
        Which rule applies is exact. Which courier it then picks is indicative — real allocation also
        checks serviceability, courier limits and live courier health, and PACE scores on data this
        screen cannot see.
      </Typography>
    </Box>
  );
}
